import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Add, Person, CheckCircle, Cancel } from "@mui/icons-material";
import { authorHeader } from "../utils/HeaderData";
import CustomTable from "../templates/CustomTable";
import { collection, getDocs, orderBy, query, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";
import { useUser } from "../components/context/UserContext";

const Author = () => {
  const navigate = useNavigate();
  const { userRole, loading } = useUser();
  const [authors, setAuthors] = useState([]);

  useEffect(() => {
    if (!loading && userRole === "operator") {
      navigate("/home");
    }
  }, [loading, userRole, navigate]);

  const fetchAuthors = async () => {
    try {
      const q = query(collection(db, "authors"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setAuthors(
        snap.docs.map((d) => ({
          _id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate() || new Date(),
        }))
      );
    } catch (err) {
      console.error(err.message);
    }
  };

  React.useEffect(() => {
    fetchAuthors();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      await updateDoc(doc(db, "authors", id), { status });
      setAuthors((prev) => prev.map((a) => (a._id === id ? { ...a, status } : a)));
      toast(
        status === "approved" ? "Author approved" : "Author declined",
        { type: status === "approved" ? "success" : "warning" }
      );
    } catch (err) {
      console.error(err);
      toast("Failed to update status", { type: "error" });
    }
  };

  const canApprove = userRole === "admin" || userRole === "superadmin";

  const extraActions = canApprove
    ? (row) => (
        <div className="flex gap-1 flex-wrap mt-0.5">
          {row.status !== "approved" && (
            <button
              onClick={() => handleStatusChange(row._id, "approved")}
              className="flex items-center gap-0.5 text-xs px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg font-abel hover:bg-green-100 transition"
            >
              <CheckCircle style={{ fontSize: 13 }} /> Approve
            </button>
          )}
          {row.status !== "approved" && row.status !== "declined" && (
            <button
              onClick={() => handleStatusChange(row._id, "declined")}
              className="flex items-center gap-0.5 text-xs px-2 py-1 bg-red-50 text-red-600 border border-red-200 rounded-lg font-abel hover:bg-red-100 transition"
            >
              <Cancel style={{ fontSize: 13 }} /> Decline
            </button>
          )}
        </div>
      )
    : null;

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#ecfdf5] flex items-center justify-center flex-shrink-0">
                <Person style={{ color: "#059669", fontSize: 20 }} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">Authors</h1>
                <p className="text-gray-400 text-xs font-abel">
                  Manage article authors
                  {canApprove && (
                    <span className="ml-2 text-[#000C66] font-semibold">· Approver access enabled</span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/create-author")}
              className="flex items-center justify-center gap-2 bg-[#000C66] text-white px-5 py-2.5 rounded-xl hover:bg-blue-900 transition font-abel text-sm font-semibold w-full sm:w-auto"
            >
              <Add style={{ fontSize: 18 }} /> Add Author
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <CustomTable
              headers={authorHeader}
              data={authors}
              editPath="/update-author"
              deletePath="/delete-author"
              extraActions={extraActions}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Author;
